import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCreateOverride, useUpdateOverride } from "@/hooks/useOverrides";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import { FIELD_NAME_OPTIONS } from "@/types/overrides";
import type { VariantOverride, FieldName } from "@/types/overrides";

interface OverrideFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  override?: VariantOverride | null;
  initialValues?: Partial<{
    source: string;
    external_variant_id: string;
    field_name: FieldName | "";
    notes: string;
  }>;
}

const EMPTY_FORM = {
  source: "",
  external_variant_id: "",
  field_name: "" as FieldName | "",
  match_value: "",
  override_value: "",
  tube_position: "",
  notes: "",
  is_active: true,
};

export function OverrideFormDialog({
  open,
  onOpenChange,
  override,
  initialValues,
}: OverrideFormDialogProps) {
  const isEdit = override != null;

  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) {
      if (override) {
        setForm({
          source: override.source,
          external_variant_id: override.external_variant_id,
          field_name: override.field_name,
          match_value: override.match_value,
          override_value: override.override_value,
          tube_position: override.tube_position ?? "",
          notes: override.notes ?? "",
          is_active: override.is_active,
        });
      } else {
        setForm({ ...EMPTY_FORM, ...initialValues });
      }
    }
  }, [open, override]);

  const { data: sources = [] } = useScrapersSources();
  const createMutation = useCreateOverride();
  const updateMutation = useUpdateOverride();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async () => {
    if (
      !form.source ||
      !form.external_variant_id ||
      !form.field_name ||
      !form.match_value ||
      !form.override_value
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          id: override.id,
          body: {
            field_name: form.field_name as FieldName,
            override_value: form.override_value,
            tube_position: form.tube_position || null,
            notes: form.notes || null,
            is_active: form.is_active,
          },
        });
        toast.success("Override updated");
      } else {
        await createMutation.mutateAsync({
          source: form.source,
          external_variant_id: form.external_variant_id,
          field_name: form.field_name as FieldName,
          match_value: form.match_value,
          override_value: form.override_value,
          tube_position: form.tube_position || null,
          notes: form.notes || null,
        });
        toast.success("Override created");
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An error occurred");
    }
  };

  const set = (key: keyof typeof EMPTY_FORM) => (value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Override" : "New Override"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="source">
                Source <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.source}
                onValueChange={set("source")}
                disabled={isEdit}
              >
                <SelectTrigger id="source" className="mt-1">
                  <SelectValue placeholder="Select source..." />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="external_variant_id">
                External Variant ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="external_variant_id"
                className="mt-1"
                placeholder="e.g. 12565"
                value={form.external_variant_id}
                onChange={(e) => set("external_variant_id")(e.target.value)}
                disabled={isEdit}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="field_name">
              Field <span className="text-destructive">*</span>
            </Label>
            <Select value={form.field_name} onValueChange={set("field_name")}>
              <SelectTrigger id="field_name" className="mt-1">
                <SelectValue placeholder="Select field..." />
              </SelectTrigger>
              <SelectContent>
                {FIELD_NAME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="match_value">
                Match Value <span className="text-destructive">*</span>
              </Label>
              <Input
                id="match_value"
                className="mt-1"
                placeholder="e.g. 63"
                value={form.match_value}
                onChange={(e) => set("match_value")(e.target.value)}
                disabled={isEdit}
              />
            </div>
            <div>
              <Label htmlFor="override_value">
                Override Value <span className="text-destructive">*</span>
              </Label>
              <Input
                id="override_value"
                className="mt-1"
                placeholder="e.g. 0.63"
                value={form.override_value}
                onChange={(e) => set("override_value")(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="tube_position">Tube Position (optional)</Label>
            <Input
              id="tube_position"
              className="mt-1"
              placeholder="e.g. Left, Right, Top-Left..."
              value={form.tube_position}
              onChange={(e) => set("tube_position")(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              className="mt-1"
              placeholder="Explain why this override exists..."
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              rows={3}
            />
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) =>
                  set("is_active")(Boolean(checked))
                }
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Active
              </Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {isEdit ? "Save Changes" : "Create Override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
